import { useReactiveVar } from '@apollo/client';
import useModal from '@area2k/use-modal';
import { faFileAlt, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import NewPayrollModalV2 from './NewPayrollModalV2';
import PayrollStatusBadge from '../Payrolls/PayrollStatusBadge';
import PageSkeleton from '../Payrolls/Skeletons/ListPayrollsSkeleton';

import Card from '@/components/Card';
import EmptyState from '@/components/EmptyState';
import Page from '@/components/Page';
import Paginator from '@/components/Paginator/Paginator';
import Stack from '@/components/Stack';
import Table from '@/components/Table';
import TableHeader from '@/components/TableHeader';
import { Small } from '@/components/Typography';
import { TableCell, TableRow } from '@/elements/Table';
import useMediaQuery from '@/hooks/useMediaQuery';
import { currentTenantVar } from '@/util/apollo/cache';
import { formatDate, formatHumanDate, formatDateTime } from '@/util/datetime';
import { parseISO, format as formatDateFns } from 'date-fns';
import { DATE_FORMAT } from '@/constants/datetime';
import Alert from '@/components/Alert';
import { useFeatureValue } from '@growthbook/growthbook-react';
import { FEATURE_TOGGLE } from '@/constants/featuretoggle';
import { useAppSelector, useAppDispatch } from '@/hooks/store';
import {
  ReloadInvoicePayrollStatusEnum,
  setReloadPayrolls,
} from '@/store/slices/pubnubSlice';
import { axiosRubyClient } from '@/util/axios/axiosClient';
import { PayrollStatusEnum } from '@/types/graphql';

type PayrollItem = {
  id: number;
  tenant_id: number;
  check_payroll_id: string;
  payday: string;
  approval_deadline: string;
  period_start: string;
  period_end: string;
  status: string;
  payroll_type: string | null;
  approved_at: string | null;
  description: string | null;
  created_at: string;
  updated_at: string;
};

type PayrollsResponse = {
  success: boolean;
  data: {
    payrolls: PayrollItem[];
    pagination: {
      current_page: number;
      per_page: number;
      total_pages: number;
      total_count: number;
    };
  };
};

export const payrollHeaderFields = [
  'ID',
  'Payday',
  'Period Start',
  'Period End',
  'Approval Deadline',
  'Approved on',
  'Payroll Status',
];

const PayrollsV2 = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [payrollInitiatedMessage, setPayrollInitiatedMessage] = useState(false);
  const [data, setData] = useState<PayrollsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const payrolllBackgroundJob = useFeatureValue(
    FEATURE_TOGGLE.EnablePayrollProcessBackgroundJob,
    false,
  );
  const navigate = useNavigate();

  const currentTenant = useReactiveVar(currentTenantVar);
  const tenantId = currentTenant!.id;
  const phoneOnly = useMediaQuery('(max-width: 559px)');
  const reloadPayrolls = useAppSelector((state) => state.pubnub.reloadPayrolls);
  const dispatch = useAppDispatch();

  const fetchPayrolls = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axiosRubyClient.get<PayrollsResponse>(
        '/api/payrolls',
        {
          params: {
            tenantId,
            page: currentPage,
            per_page: itemsPerPage,
          },
        },
      );
      // Handle the API response structure: { success: true, data: { payrolls: [], pagination: {} } }
      if (response.data?.success && response.data?.data) {
        setData(response.data);
      } else {
        setData({
          success: false,
          data: {
            payrolls: [],
            pagination: {
              current_page: 1,
              per_page: itemsPerPage,
              total_pages: 0,
              total_count: 0,
            },
          },
        });
      }
    } catch (error) {
      console.error('Error fetching payrolls:', error);
      setData({
        success: false,
        data: {
          payrolls: [],
          pagination: {
            current_page: 1,
            per_page: itemsPerPage,
            total_pages: 0,
            total_count: 0,
          },
        },
      });
    } finally {
      setLoading(false);
    }
  }, [tenantId, currentPage, itemsPerPage]);

  const handlePageChange = (pageNumber: number) => setCurrentPage(pageNumber);

  const handleNumberItemsChange = (event) => {
    setCurrentPage(1);
    setItemsPerPage(parseInt(event.target.value));
  };

  const items = data?.data?.payrolls || [];
  const pageInfo = data?.data?.pagination || {
    current_page: 1,
    per_page: itemsPerPage,
    total_pages: 0,
    total_count: 0,
  };

  useEffect(() => {
    if (reloadPayrolls !== ReloadInvoicePayrollStatusEnum.OFF) {
      dispatch(setReloadPayrolls(ReloadInvoicePayrollStatusEnum.OFF));
      setPayrollInitiatedMessage(false);
    }

    fetchPayrolls();
  }, [
    tenantId,
    currentPage,
    itemsPerPage,
    reloadPayrolls,
    fetchPayrolls,
    dispatch,
  ]);

  const [showNewPayrollModal, hideNewPayrollModal] = useModal(
    () => (
      <NewPayrollModalV2
        hideModal={hideNewPayrollModal}
        setPayrollInitiatedMessage={setPayrollInitiatedMessage}
        onPayrollCreated={fetchPayrolls}
      />
    ),
    [fetchPayrolls],
  );

  const formatDateSafe = useCallback((dateString: string | null) => {
    if (!dateString) return 'N/A';
    try {
      return formatDateFns(parseISO(dateString), DATE_FORMAT);
    } catch (e) {
      return dateString;
    }
  }, []);

  const formatDateTimeSafe = useCallback((dateString: string | null) => {
    if (!dateString) return 'N/A';
    try {
      return formatDateTime(dateString);
    } catch (e) {
      return dateString;
    }
  }, []);

  const renderPayrolls = useCallback(
    (payroll: PayrollItem, phoneOnly: boolean) => {
      return (
        <TableRow
          key={payroll.id}
          clickable
          mobile={phoneOnly}
          onClick={() =>
            navigate(`/payrolls-v2/${payroll.id}?status=${payroll.status}`)
          }
        >
          <TableCell noCellTitle stack>
            <Stack
              align={'start'}
              gap={1}
              justify={'start'}
              vertical={!phoneOnly}
            >
              {phoneOnly && (
                <Small size={'lg'} weight={'semibold'}>
                  ID:{' '}
                </Small>
              )}
              <Small
                color={'theme'}
                size={phoneOnly ? 'lg' : 'md'}
                weight={phoneOnly ? 'semibold' : 'normal'}
              >
                {payroll.id}
              </Small>
            </Stack>
          </TableCell>
          <TableCell data-celltitle={'Payday: '} stack={phoneOnly}>
            {formatDateSafe(payroll.payday)}
          </TableCell>
          <TableCell data-celltitle={'Period Start: '} stack={phoneOnly}>
            {formatDateSafe(payroll.period_start)}
          </TableCell>
          <TableCell data-celltitle={'Period End: '} stack={phoneOnly}>
            {formatDateSafe(payroll.period_end)}
          </TableCell>
          <TableCell data-celltitle={'Approval Deadline: '} stack={phoneOnly}>
            {formatDateTimeSafe(payroll.approval_deadline)}
          </TableCell>
          <TableCell data-celltitle={'Approved On: '} stack={phoneOnly}>
            {formatDateTimeSafe(payroll.approved_at)}
          </TableCell>
          <TableCell data-celltitle={'Payroll Status: '} stack={phoneOnly}>
            <Stack>
              <PayrollStatusBadge
                status={
                  (payroll.status.toUpperCase() as PayrollStatusEnum) ||
                  PayrollStatusEnum.DRAFT
                }
              />
            </Stack>
          </TableCell>
        </TableRow>
      );
    },
    [],
  );

  if (loading || !data) {
    return <PageSkeleton />;
  }

  const payrollItems = data?.data?.payrolls || [];
  const pageInfoData = data?.data?.pagination || {
    current_page: 1,
    per_page: itemsPerPage,
    total_pages: 0,
    total_count: 0,
  };

  return (
    <Page
      headerPadding={!!phoneOnly}
      noPadding={!!phoneOnly}
      primaryAction={{
        a11yLabel: 'Create a Preview Payroll',
        label: 'Run New Payroll',
        id: 'new-payroll-btn',
        onAction: showNewPayrollModal,
      }}
      size={phoneOnly ? 'full' : 'lg'}
      title="Payroll Runs v2"
    >
      {payrolllBackgroundJob && payrollInitiatedMessage && (
        <Alert
          description="Your payroll is being generated. This process may take a minute to complete. Please refresh the page after a moment to view the results. Do not attempt to generate the payroll again while the process is in progress."
          icon={faInfoCircle}
          status="info"
        />
      )}
      <Card noRadius={phoneOnly}>
        <Table>
          {payrollItems.length > 0 ? (
            <>
              {!phoneOnly && <TableHeader fields={payrollHeaderFields} />}
              <tbody>
                {payrollItems.map((i) => {
                  return renderPayrolls(i, phoneOnly);
                })}
              </tbody>
            </>
          ) : (
            <Stack justify="center">
              <EmptyState
                icon={faFileAlt}
                text={`Click "Run New Payroll" to add`}
                title="No payrolls have been created yet"
              />
            </Stack>
          )}
        </Table>
      </Card>
      {items.length > 0 && (
        <Paginator
          currentPage={currentPage}
          handleNumberItemsChange={handleNumberItemsChange}
          handlePageChange={handlePageChange}
          itemsLength={pageInfoData.total_count as number}
          itemsPerPage={itemsPerPage}
        />
      )}
    </Page>
  );
};

export default PayrollsV2;
